type ErrorReportItem = {
  key: string
  type: string
  message: string
  stack: string
  route: string
  pageUrl: string
  userAgent: string
  info: string
  firstAt: string
  lastAt: string
  count: number
}

type SendPayload = {
  source: 'nuxt-client'
  reason: 'immediate' | 'interval' | 'pagehide'
  items: ErrorReportItem[]
}

type CircuitBreakerState = {
  consecutive: number
  lastAtMs: number
  silencedUntilMs: number
}

const FLUSH_INTERVAL_MS = 10 * 1000
const MAX_QUEUE_SIZE = 200
const DUPLICATE_WINDOW_MS = 1200
const REPORT_ENDPOINT = '/api/error/report'
const CIRCUIT_BREAKER_THRESHOLD = 10
const CIRCUIT_BREAKER_SILENCE_MS = 60 * 60 * 1000
const CIRCUIT_BREAKER_RESET_GAP_MS = 5 * 60 * 1000
const CIRCUIT_BREAKER_MAX_KEYS = 1200

const clip = (value: string, max = 1200) => {
  if (!value) return ''
  return value.length > max ? `${value.slice(0, max)}...` : value
}

const buildStackFingerprint = (stack: string) => {
  const firstCallLine = stack.split('\n').find((line) => line.includes('at ') || line.includes('@')) || ''
  return firstCallLine.replace(/-[a-zA-Z0-9]{8}\.(mjs|js)/g, '.$1').trim()
}

const toError = (unknownError: unknown): Error => {
  if (unknownError instanceof Error) return unknownError
  if (typeof unknownError === 'string') return new Error(unknownError)
  try {
    return new Error(JSON.stringify(unknownError))
  } catch {
    return new Error(String(unknownError))
  }
}

const shouldIgnoreClientError = (message: string, stack: string) => {
  const msg = (message || '').toLowerCase()
  const stLower = (stack || '').toLowerCase()

  if (msg === 'script error.' && !stLower) return true

  if (msg.includes('resizeobserver loop completed with undelivered notifications')) return true
  if (msg.includes('resizeobserver loop limit exceeded')) return true

  if (msg.includes('aborterror') && (msg.includes('aborted') || msg.includes('cancel'))) return true
  if (msg.includes('the user aborted a request')) return true

  if (msg.includes('non-error promise rejection captured')) return true
  if (msg.includes('unhandledrejection: non-error promise rejection captured')) return true

  if (import.meta.dev) {
    if (stLower.includes('/@vite/client') || stLower.includes('hmrclient.')) return true
    if (stLower.includes('virtual:nuxt:') && stLower.includes('hmr')) return true
  }
  return false
}

const scheduleOffMain = (task: () => void) => {
  queueMicrotask(() => {
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      const run = window.requestIdleCallback as (cb: IdleRequestCallback, opts?: IdleRequestOptions) => number
      run(() => task(), { timeout: 1000 })
      return
    }
    setTimeout(task, 0)
  })
}

const sendPayload = async (payload: SendPayload) => {
  const body = JSON.stringify(payload)
  if (navigator.sendBeacon && body.length <= 60000) {
    const blob = new Blob([body], { type: 'application/json; charset=utf-8' })
    navigator.sendBeacon(REPORT_ENDPOINT, blob)
    return
  }
  await fetch(REPORT_ENDPOINT, {
    method: 'POST',
    body,
    keepalive: true,
    headers: {
      'Content-Type': 'application/json; charset=utf-8'
    }
  })
}

export default defineNuxtPlugin((nuxtApp) => {
  if (!import.meta.client) return
  const router = useRouter()

  const queueMap = new Map<string, ErrorReportItem>()
  const recentlyDispatchedAtByKey = new Map<string, number>()
  const circuitBreakerByKey = new Map<string, CircuitBreakerState>()
  let flushTimer: number | null = null
  let lastSentAtMs = 0

  const stopTimer = () => {
    if (flushTimer === null) return
    window.clearTimeout(flushTimer)
    flushTimer = null
  }

  const rememberDispatched = (keys: string[], atMs = Date.now()) => {
    for (const key of keys) recentlyDispatchedAtByKey.set(key, atMs)
    if (recentlyDispatchedAtByKey.size <= 600) return
    for (const [key, ts] of recentlyDispatchedAtByKey) {
      if (atMs - ts > FLUSH_INTERVAL_MS) recentlyDispatchedAtByKey.delete(key)
    }
  }

  const compactCircuitBreaker = (nowMs: number) => {
    if (circuitBreakerByKey.size <= CIRCUIT_BREAKER_MAX_KEYS) return
    for (const [key, state] of circuitBreakerByKey) {
      const silenceExpired = state.silencedUntilMs <= nowMs
      const stale = nowMs - state.lastAtMs > CIRCUIT_BREAKER_RESET_GAP_MS
      if (silenceExpired && stale) circuitBreakerByKey.delete(key)
      if (circuitBreakerByKey.size <= CIRCUIT_BREAKER_MAX_KEYS) return
    }
  }

  const isSilencedByCircuitBreaker = (key: string, nowMs: number) => {
    const state = circuitBreakerByKey.get(key)
    if (!state) {
      circuitBreakerByKey.set(key, {
        consecutive: 1,
        lastAtMs: nowMs,
        silencedUntilMs: 0
      })
      compactCircuitBreaker(nowMs)
      return false
    }
    const prevAtMs = state.lastAtMs
    state.lastAtMs = nowMs
    if (state.silencedUntilMs > nowMs) return true
    if (nowMs - prevAtMs > CIRCUIT_BREAKER_RESET_GAP_MS) {
      state.consecutive = 0
    }
    state.consecutive += 1
    compactCircuitBreaker(nowMs)
    if (state.consecutive < CIRCUIT_BREAKER_THRESHOLD) return false
    state.consecutive = 0
    state.silencedUntilMs = nowMs + CIRCUIT_BREAKER_SILENCE_MS
    return true
  }

  const flush = async (reason: SendPayload['reason']) => {
    if (queueMap.size === 0) {
      stopTimer()
      return
    }
    const items = Array.from(queueMap.values())
      .sort((a, b) => new Date(a.lastAt).getTime() - new Date(b.lastAt).getTime())
      .slice(0, 30)
    for (const item of items) {
      queueMap.delete(item.key)
    }
    const payload: SendPayload = {
      source: 'nuxt-client',
      reason,
      items
    }
    try {
      await sendPayload(payload)
    } catch {
    } finally {
      const nowMs = Date.now()
      lastSentAtMs = nowMs
      rememberDispatched(
        items.map((item) => item.key),
        nowMs
      )
      if (queueMap.size === 0) stopTimer()
    }
  }

  const enqueue = (item: Omit<ErrorReportItem, 'count' | 'firstAt' | 'lastAt'>) => {
    const now = new Date().toISOString()
    const exists = queueMap.get(item.key)
    if (exists) {
      exists.count += 1
      exists.lastAt = now
      exists.type = item.type
      exists.info = item.info
      return
    }
    if (queueMap.size >= MAX_QUEUE_SIZE) {
      const oldestKey = queueMap.keys().next().value
      if (oldestKey) queueMap.delete(oldestKey)
    }
    queueMap.set(item.key, {
      ...item,
      count: 1,
      firstAt: now,
      lastAt: now
    })
  }

  const ensureFlushAt = (targetAtMs: number) => {
    const delay = Math.max(0, targetAtMs - Date.now())
    if (flushTimer !== null) return
    flushTimer = window.setTimeout(() => {
      flushTimer = null
      scheduleOffMain(() => {
        void flush('interval')
      })
    }, delay)
  }

  const capture = (type: string, errorLike: unknown, info = '') => {
    const route = router.currentRoute.value.fullPath || ''
    const pageUrl = window.location.href
    const userAgent = navigator.userAgent
    const err = toError(errorLike)
    const message = clip(err.message || String(errorLike || 'Unknown Error'), 600)
    const stack = clip(err.stack || '', 2200)
    if (shouldIgnoreClientError(message, stack)) return
    const stackFp = buildStackFingerprint(stack)
    const fingerprint = stackFp || clip(info, 200)
    const key = `${route}|${message}|${fingerprint}`
    const nowMs = Date.now()
    if (isSilencedByCircuitBreaker(key, nowMs)) return
    const itemBase = {
      key,
      type,
      message,
      stack,
      route,
      pageUrl,
      userAgent,
      info: clip(info, 400)
    }

    const lastDispatchedAt = recentlyDispatchedAtByKey.get(key)
    if (lastDispatchedAt !== undefined && nowMs - lastDispatchedAt < DUPLICATE_WINDOW_MS) return
    if (nowMs - lastSentAtMs >= FLUSH_INTERVAL_MS) {
      lastSentAtMs = nowMs
      rememberDispatched([key], nowMs)
      scheduleOffMain(() => {
        const now = new Date().toISOString()
        const immediatePayload: SendPayload = {
          source: 'nuxt-client',
          reason: 'immediate',
          items: [
            {
              ...itemBase,
              count: 1,
              firstAt: now,
              lastAt: now
            }
          ]
        }
        void sendPayload(immediatePayload).catch(() => {})
      })
      return
    }

    enqueue(itemBase)
    ensureFlushAt(lastSentAtMs + FLUSH_INTERVAL_MS)
  }

  nuxtApp.hook('app:error', (error) => {
    capture('app:error', error)
  })

  nuxtApp.hook('vue:error', (error, _target, info) => {
    capture('vue:error', error, String(info || ''))
  })

  window.addEventListener('error', (event) => {
    const info = `${event.filename || ''}:${event.lineno || 0}:${event.colno || 0}`
    capture('window:error', event.error || event.message, info)
  })

  window.addEventListener('unhandledrejection', (event) => {
    capture('window:unhandledrejection', event.reason)
  })

  window.addEventListener('pagehide', () => {
    scheduleOffMain(() => {
      void flush('pagehide')
    })
  })
})
